
import { useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/hooks/use-toast';
import { Task } from '@/types/lead';
import { completeTask, deleteTask, updateTask } from '@/services/leads/taskService';
import { useLoading } from '@/context/LoadingContext';

export const useTaskMutations = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { startLoading, stopLoading } = useLoading();

  const handleSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['tasks'] });
  };

  const handleError = (error: Error) => {
    toast({
      title: 'Error',
      description: error.message,
      variant: 'destructive',
    });
  };

  const completeMutation = async (taskId: string) => {
    const operationId = `complete-task-${taskId}`;
    startLoading(operationId, 'Completing task...');
    
    try {
      const task = await completeTask(taskId);
      toast({
        title: 'Task Completed',
        description: `"${task.title}" marked as completed`,
        variant: 'default',
      });
      handleSuccess();
      return task;
    } catch (error) {
      handleError(error as Error);
      throw error;
    } finally {
      stopLoading(operationId);
    }
  };

  const deleteMutation = async (taskId: string) => {
    const operationId = `delete-task-${taskId}`;
    startLoading(operationId, 'Deleting task...');
    
    try {
      await deleteTask(taskId);
      toast({
        title: 'Success',
        description: 'Task deleted successfully',
      });
      handleSuccess();
    } catch (error) {
      handleError(error as Error);
    } finally {
      stopLoading(operationId);
    }
  };

  const updateMutation = async (taskId: string, updates: Partial<Task>) => {
    const operationId = `update-task-${taskId}`;
    startLoading(operationId, 'Updating task...');
    
    try {
      const task = await updateTask(taskId, updates);
      toast({
        title: 'Success',
        description: 'Task updated successfully',
      });
      handleSuccess();
      return task;
    } catch (error) {
      handleError(error as Error);
      throw error;
    } finally {
      stopLoading(operationId);
    }
  };

  return {
    completeMutation,
    deleteMutation,
    updateMutation,
  };
};
